import { useEffect, useMemo, useRef, useState } from "react";
import { X, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

let infoP = null;
const caricaInfo = () => (infoP ||= api.get("/persone-info").then((r) => Object.fromEntries(r.data.map((p) => [p.id, p]))).catch(() => { infoP = null; return {}; }));
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const GRUPPI = [["soci", "Soci", (p) => p.socio], ["direttori", "Direttori", (p) => p.direttore], ["segreteria", "Segreteria", (p) => p.segreteria]];

export function PersonaSearch({ people, sel, onChange, fissi = [], single = false, gruppi = true, testid = "persona-search", placeholder = "Cerca persona…" }) {
  const { user } = useAuth();
  const [info, setInfo] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => { caricaInfo().then(setInfo); }, []);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, []);
  const scegli = useMemo(() => (people || []).filter((p) => info[p.id]).map((p) => ({ ...p, ...info[p.id], name: p.name || info[p.id].name })), [people, info]);
  const byId = useMemo(() => Object.fromEntries(scegli.map((p) => [p.id, p])), [scegli]);
  const presi = new Set([...sel, ...fissi]);
  const nq = norm(q.trim());
  useEffect(() => {
    if (!open || !nq) return undefined;
    const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); setOpen(false); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [open, nq]);
  const risultati = nq ? scegli.filter((p) => !presi.has(p.id) && (norm(p.name).split(/\s+/).some((w) => w.startsWith(nq)) || (nq.includes(" ") && norm(p.name).includes(nq)))).slice(0, 8) : [];
  const add = (id) => { if (!single && sel.includes(id)) return; onChange(single ? [id] : [...sel, id]); setQ(""); setOpen(false); };
  const togli = (id) => onChange(sel.filter((x) => x !== id));
  const mieiTeam = new Set([user?.team_id, ...(user?.team_ids || [])].filter(Boolean));
  const gr = [...GRUPPI, ...(mieiTeam.size ? [["team", "Il mio team", (p) => p.id !== user?.id && (p.team_ids || []).some((t) => mieiTeam.has(t))]] : [])];
  const aggiungiGruppo = (f) => onChange([...new Set([...sel, ...scegli.filter((p) => f(p) && !fissi.includes(p.id)).map((p) => p.id)])]);
  const label = (p) => [p.name, p.ruolo, p.team].filter(Boolean).join(" · ");
  return (
    <div ref={box} data-testid={testid} className="space-y-2">
      {(sel.length > 0 || fissi.length > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-scelti`}>
          {fissi.map((id) => <span key={id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">{byId[id]?.name || info[id]?.name || "Persona"}</span>)}
          {sel.map((id) => (
            <span key={id} data-testid={`${testid}-chip-${id}`} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-semibold bg-[#FC82ED]/15 border border-[#FC82ED] text-[#2B2B2B]">
              {byId[id]?.name || info[id]?.name || "Persona"}
              <button type="button" data-testid={`${testid}-togli-${id}`} onClick={() => togli(id)} aria-label="Togli" className="p-0.5 rounded-full hover:bg-[#FC82ED]/30"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {(!single || sel.length === 0) && (
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input data-testid={`${testid}-input`} value={q} placeholder={placeholder} autoComplete="off"
                 onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (risultati[0]) add(risultati[0].id); } if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); } }}
                 className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-base sm:text-sm" />
          {open && nq && (
            <div data-testid={`${testid}-menu`} data-persona-open="1" className="absolute z-[80] left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-[var(--border)] bg-white shadow-xl">
              {risultati.length === 0 ? <div className="px-3 py-3 text-sm text-[var(--muted)]">Nessuna persona trovata.</div> : risultati.map((p) => (
                <button type="button" key={p.id} data-testid={`${testid}-opt-${p.id}`} onClick={() => add(p.id)}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#FC82ED]/10 active:bg-[#FC82ED]/20 border-b border-slate-50 last:border-0">
                  <span className="font-semibold">{p.name}</span><span className="text-[var(--muted)]">{label(p).slice(p.name.length)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!single && gruppi && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-gruppi`}>
          {gr.map(([k, l, f]) => scegli.some((p) => f(p) && !presi.has(p.id)) && (
            <button type="button" key={k} data-testid={`${testid}-gruppo-${k}`} onClick={() => aggiungiGruppo(f)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[var(--border)] text-slate-600 hover:border-[#FC82ED] hover:text-[#2B2B2B]">+ {l}</button>
          ))}
        </div>
      )}
    </div>
  );
}
